import React, { useCallback, useEffect, useRef, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { AxiosGetMethod, AxiosPostMethod } from "../helpers/customAxios";
import { ErrorToast, SuccessToast } from "../components/toastify";
import {
  FaArrowLeft,
  FaArrowRight,
  FaCheck,
  FaBuilding,
  FaCheckCircle,
  FaEye,
  FaEyeSlash,
  FaIdCard,
  FaMapMarkerAlt,
  FaUserTie,
} from "react-icons/fa";
import { Link, useNavigate, useParams } from "react-router-dom";
import DesktopFileUpload from "../components/fileupload";
import convertoformdata from "../helpers/convertToFormdata";
import { setShowPreloader } from "../store/activePage";
import { useDispatch, useSelector } from "react-redux";
import PaymentComponent from "../components/payment-gateway";
import CitySearchSelect from "../components/city-search-select";
import { setTypingMode } from "../store/activePage";

const inputClass =
  "w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-orange-500 focus:ring-4 focus:ring-orange-100";

const errorClass = "mt-1.5 text-xs font-semibold text-red-600";

const getApiMessage = (res, fallback) => {
  const data = res?.data?.data;
  if (Array.isArray(data?.messages) && data.messages.length) {
    return data.messages.join(" ");
  }
  if (typeof data?.error === "string") return data.error;
  if (Array.isArray(data) && data.length) return data.join(" ");
  return res?.data?.message || fallback;
};

const STEP_CONFIG = [
  {
    id: "owner",
    number: "01",
    title: "Owner Details",
    description: "Primary enterprise account information.",
    icon: FaUserTie,
    fields: [
      "first_name",
      "last_name",
      "mobile",
      "email",
      "password",
      "plot_no",
    ],
  },
  {
    id: "address",
    number: "02",
    title: "Owner Address",
    description: "Address used for account records.",
    icon: FaMapMarkerAlt,
    fields: ["address", "city", "state", "pincode"],
  },
  {
    id: "organisation",
    number: "03",
    title: "Organisation Details",
    description: "Business information and verification.",
    icon: FaBuilding,
    fields: [
      "organisation_name",
      "organisation_address",
      "organisation_city",
      "organisation_state",
      "organisation_pincode",
      "organisation_aadhaar_number",
      "organisation_pan_number",
      "proof",
    ],
  },
];

const SectionHeader = ({ icon: Icon, title, description }) => (
  <div className="mb-5 flex items-start gap-3">
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-orange-50 text-orange-700 ring-1 ring-orange-100">
      <Icon />
    </span>
    <div>
      <h2 className="text-lg font-black tracking-tight text-slate-950">
        {title}
      </h2>
      <p className="mt-1 text-sm leading-6 text-slate-500">{description}</p>
    </div>
  </div>
);

const FormField = ({ label, name, type = "text", forceTamil = false, noTranslate = false, className = "", children }) => (
  <div className={className}>
    <label htmlFor={name} className="mb-1.5 block text-sm font-bold text-slate-700">
      {label}
    </label>
    {children || (
      <Field
        id={name}
        name={name}
        type={type}
        className={`${inputClass} ${forceTamil ? "force-tamil" : ""} ${noTranslate ? "no-translate" : ""}`}
      />
    )}
    <ErrorMessage name={name} component="div" className={errorClass} />
  </div>
);

const OperatorRegister = () => {
  const { refferal_code_org } = useParams();
  const refferral_ref = useRef();
  const [refOrgInfo, setRefOrgInfo] = useState({ id: 0, name: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [registrationPaymentContext, setRegistrationPaymentContext] = useState(null);
  const [contactAvailability, setContactAvailability] = useState(null);
  const [checkingContactAvailability, setCheckingContactAvailability] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const typingMode = useSelector((state) => state.activePage.typingMode || "tanglish");

  const initialValues = {
    first_name: "",
    last_name: "",
    mobile: "",
    email: "",
    password: "",
    plot_no: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    organisation_name: "",
    organisation_address: "",
    organisation_city: "",
    organisation_state: "",
    organisation_pincode: "",
    organisation_aadhaar_number: "",
    organisation_pan_number: "",
    refferral_code: refferal_code_org || "",
    refferral_org_id: "",
    refferral_org_name: "",
    proof: "",
  };

  const validationSchema = Yup.object({
    first_name: Yup.string().required("First Name is required"),
    last_name: Yup.string().required("Last Name is required"),
    mobile: Yup.string()
      .matches(/^\d{10}$/, "Mobile number must be 10 digits")
      .required("Mobile number is required"),
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    password: Yup.string()
      .min(8, "Password must be at least 8 characters")
      .required("Password is required"),
    plot_no: Yup.string().required("Plot No. is required"),
    address: Yup.string().required("Address is required"),
    city: Yup.string().required("City is required"),
    state: Yup.string().required("State is required"),
    pincode: Yup.string()
      .matches(/^\d{6}$/, "Pincode must be 6 digits")
      .required("Pincode is required"),
    organisation_name: Yup.string().required("Organisation Name is required"),
    organisation_address: Yup.string().required(
      "Organisation Address is required",
    ),
    organisation_city: Yup.string().required("Organisation City is required"),
    organisation_state: Yup.string().required("Organisation State is required"),
    organisation_pincode: Yup.string()
      .matches(/^\d{6}$/, "Pincode must be 6 digits")
      .required("Organisation Pincode is required"),
    organisation_aadhaar_number: Yup.string()
      .required("Aadhaar number is required")
      .matches(/^\d{12}$/, "Aadhaar number must be 12 digits"),
    organisation_pan_number: Yup.string()
      .matches(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, "Invalid PAN number")
      .required("PAN number is required"),
    refferral_code: Yup.string(),
    refferral_org_id: Yup.string(),
    refferral_org_name: Yup.string(),
    proof: Yup.mixed()
      .required("Verification document is required")
      .test(
        "fileType",
        "Only image or PDF files are allowed",
        (value) =>
          value &&
          [
            "image/jpeg",
            "image/jpg",
            "image/png",
            "image/webp",
            "application/pdf",
          ].includes(value.type),
      )
      .test(
        "fileSize",
        "File size must be less than 1MB",
        (value) => value && value.size <= 1048576,
      ),
  });

  const registrationFee =
    Number(process.env.REACT_APP_OPERATOR_REGISTRATION_FEE || 1) || 1;

  const continueToLogin = useCallback(
    (mobile) => {
      localStorage.setItem("otp-verification-mobile", mobile);
      SuccessToast(
        "Registration submitted. Your organisation will be activated after admin review."
      );
      navigate("/login");
    },
    [navigate],
  );

  const handleRegistrationPayment = useCallback(async () => {
    const orgId = registrationPaymentContext?.orgId;
    const mobile = registrationPaymentContext?.mobile;
    if (!orgId || !mobile) {
      ErrorToast("Registration details are missing. Please try again.");
      return;
    }

    dispatch(setShowPreloader(true));
    const orderRes = await AxiosPostMethod(
      "org/registration-payment/order-create",
      { org_id: orgId },
      true,
    );
    dispatch(setShowPreloader(false));

    if (orderRes?.status === 200 && orderRes?.data?.data?.order_id) {
      PaymentComponent(
        orderRes.data.data,
        async (razval) => {
          await AxiosPostMethod(
            "org/registration-payment/update",
            { ...razval, org_id: orgId },
            true,
          );
          SuccessToast("Registration payment completed.");
          continueToLogin(mobile);
        },
        () => {
          ErrorToast(
            "Registration payment was skipped. It will be requested again on first login.",
          );
        },
      );
      return;
    }

    ErrorToast(
      orderRes?.data?.message ||
        "Unable to create registration payment order. Please try again.",
    );
  }, [continueToLogin, dispatch, registrationPaymentContext]);

  const onSubmit = async (values) => {
    const bodyObj = {
      first_name: values.first_name,
      last_name: values.last_name,
      mobile: values.mobile,
      email: values.email,
      password: values.password,
      plot_no: values.plot_no,
      address: values.address,
      city: values.city,
      state: values.state,
      pincode: values.pincode,
      organisation_name: values.organisation_name,
      organisation_address: values.organisation_address,
      organisation_city: values.organisation_city,
      organisation_state: values.organisation_state,
      organisation_pincode: values.organisation_pincode,
      aadhaar_number: values.organisation_aadhaar_number,
      pan_number: values.organisation_pan_number,
      referred_by: values.refferral_org_id || refOrgInfo.id,
      proof: values.proof,
      verified: 1,
    };
    const body = convertoformdata(bodyObj);

    if (
      values.refferral_code?.length > 0 &&
      values.refferral_org_name?.length === 0 &&
      !refOrgInfo.id &&
      refOrgInfo.name?.length === 0
    ) {
      ErrorToast(
        "Please Enter valid Refferral code or Clear the current code to continue",
      );
      return null;
    } else {
      try {
        dispatch(setShowPreloader(true));
        const orgRes = await AxiosPostMethod("org/registration", body, true);
        dispatch(setShowPreloader(false));

        if (orgRes?.status === 200) {
          const orgId = orgRes?.data?.data?.org_id;
          if (!orgId) {
            continueToLogin(values.mobile);
            return;
          }
          setRegistrationPaymentContext({
            orgId,
            mobile: values.mobile,
          });
          return;
        }

        const errorMessage = getApiMessage(
          orgRes,
          "Unable to complete registration. Please try again.",
        );

        ErrorToast(errorMessage);
      } catch (error) {
        dispatch(setShowPreloader(false));
        ErrorToast("Something went wrong while registering. Please try again.");
      }
    }
  };

  const resetOrgInfo = (setFieldValue) => {
    setFieldValue("refferral_code", "");
    setFieldValue("refferral_org_id", "");
    setFieldValue("refferral_org_name", "");
  };

  const getRefferralInfo = async (event, setFieldValue, values) => {
    const input = event.target.value;
    if (input?.length === 13) {
      dispatch(setShowPreloader(true));
      const org_data = await AxiosGetMethod(
        `org/get-refferral-org?code=${input}`,
        true,
      );
      dispatch(setShowPreloader(false));
      if (org_data.status === 200) {
        const { id, name } = org_data.data.data;
        setFieldValue("refferral_org_id", id);
        setFieldValue("refferral_org_name", name);
      } else if (org_data.status === 404) {
        resetOrgInfo(setFieldValue);
        ErrorToast("Invalid Refferral Code. Please provide valid code");
      } else {
        resetOrgInfo(setFieldValue);
        ErrorToast("Server error");
      }
    } else {
      if (values.refferral_org_name?.length > 0) resetOrgInfo(setFieldValue);
    }
  };

  const validateRef = useCallback(async () => {
    dispatch(setShowPreloader(true));
    const org_data = await AxiosGetMethod(
      `org/get-refferral-org?code=${refferal_code_org}`,
      true,
    );
    dispatch(setShowPreloader(false));
    if (org_data.status === 200) {
      const { id, name } = org_data.data.data;
      setRefOrgInfo({ id, name });
    } else if (org_data.status === 404) {
      ErrorToast("Invalid Refferral Code. Please provide valid code");
    } else {
      ErrorToast("Server error");
    }
  }, [dispatch, refferal_code_org]);

  useEffect(() => {
    if (refferal_code_org) {
      if (refferral_ref.current) refferral_ref.current.disabled = true;
      validateRef();
    }
  }, [refferal_code_org, validateRef]);

  const validateStep = async (formik, stepIndex) => {
    const stepFields = STEP_CONFIG[stepIndex].fields;
    const errors = await formik.validateForm();
    const stepErrors = stepFields.filter((field) => errors[field]);

    if (stepErrors.length > 0) {
      const touchedFields = stepFields.reduce((acc, field) => {
        acc[field] = true;
        return acc;
      }, {});
      formik.setTouched({ ...formik.touched, ...touchedFields });
      return false;
    }

    return true;
  };

  const validateOwnerContactAvailability = async (formik) => {
    setCheckingContactAvailability(true);
    setContactAvailability(null);

    const mobile = String(formik.values.mobile || "").trim();
    const email = String(formik.values.email || "").trim();
    const params = new URLSearchParams({ mobile, email });
    const res = await AxiosGetMethod(
      `org/registration-contact-check?${params.toString()}`,
      true,
    );

    setCheckingContactAvailability(false);

    if (res?.status === 200) {
      setContactAvailability({
        type: "success",
        message: "Mobile number and email are valid and available.",
      });
      return true;
    }

    const data = res?.data?.data || {};
    const messages = Array.isArray(data?.messages)
      ? data.messages
      : [getApiMessage(res, "Unable to verify mobile and email availability.")];
    const message = messages.join(" ");

    if (data?.mobile_exists) {
      formik.setFieldError("mobile", "This mobile number is already registered.");
    }
    if (data?.email_exists) {
      formik.setFieldError("email", "This email is already registered.");
    }

    setContactAvailability({
      type: "error",
      message,
    });
    ErrorToast(message);
    return false;
  };

  const goToNextStep = async (formik) => {
    const isCurrentStepValid = await validateStep(formik, activeStep);
    if (!isCurrentStepValid) {
      ErrorToast("Please complete the required fields in this section");
      return;
    }

    if (activeStep === 0) {
      const isContactAvailable = await validateOwnerContactAvailability(formik);
      if (!isContactAvailable) return;
    }

    setActiveStep((current) => Math.min(current + 1, STEP_CONFIG.length - 1));
  };

  const currentStepConfig = STEP_CONFIG[activeStep];
  const isLastStep = activeStep === STEP_CONFIG.length - 1;

  const toggleTamilTyping = () => {
    const newMode = typingMode === "tanglish" ? "english" : "tanglish";
    dispatch(setTypingMode(newMode));
  };

  if (registrationPaymentContext) {
    return (
      <div className="min-h-screen bg-slate-100 px-4 py-8 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl">
          <div className="rounded-3xl border border-slate-200 bg-white shadow-xl shadow-slate-200/70">
            <div className="bg-[linear-gradient(135deg,#0f172a_0%,#132238_50%,#0f766e_100%)] px-8 py-8 text-white">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/10 text-2xl ring-1 ring-white/20">
                <FaCheckCircle />
              </div>
              <p className="mt-5 text-sm font-bold uppercase tracking-[0.22em] text-teal-200">
                Registration Created
              </p>
              <h1 className="mt-2 text-3xl font-black tracking-tight">
                One final step before login
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-200/85">
                Your organisation has been registered successfully. Complete the one-time
                registration payment now, or finish it on your first login.
              </p>
            </div>

            <div className="grid gap-6 px-8 py-8 lg:grid-cols-[1fr_0.9fr]">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">
                  Registration Fee
                </p>
                <p className="mt-3 text-4xl font-black tracking-tight text-slate-950">
                  Rs. {registrationFee}
                </p>
                <p className="mt-3 text-sm leading-6 text-slate-500">
                  Paying now keeps the account ready for smooth first login. If you skip,
                  the app will ask for payment before dashboard access.
                </p>
              </div>

              <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-6">
                <p className="text-sm font-bold text-slate-900">What happens next</p>
                <div className="mt-4 space-y-3 text-sm text-slate-600">
                  <p>1. Admin approves your organisation registration.</p>
                  <p>2. Registration fee must be paid before dashboard usage.</p>
                  <p>3. Login continues with your registered mobile and password.</p>
                </div>

                <div className="mt-6 flex flex-col gap-3">
                  <button
                    type="button"
                    onClick={handleRegistrationPayment}
                    className="inline-flex items-center justify-center rounded-xl bg-orange-600 px-5 py-3 text-sm font-bold text-white transition hover:bg-orange-700"
                  >
                    Pay Now
                  </button>
                  <button
                    type="button"
                    onClick={() => continueToLogin(registrationPaymentContext.mobile)}
                    className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 transition hover:border-slate-300"
                  >
                    Continue To Login
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-6xl">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <Link
            to="/login"
            className="inline-flex w-fit items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 no-underline shadow-sm transition hover:border-slate-300 hover:text-slate-950"
          >
            <FaArrowLeft className="text-xs" />
            Back to login
          </Link>
          <p className="text-sm font-semibold text-slate-500">
            Already registered?{" "}
            <Link
              to="/login"
              className="font-bold text-orange-700 no-underline hover:text-orange-900"
            >
              Sign in
            </Link>
          </p>
        </div>

        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-200/70">
          <div className="flex items-center justify-end gap-3 border-b border-slate-200 bg-slate-50 px-6 py-3">
            <span className="text-xs font-bold uppercase tracking-widest text-slate-500">
              Tamil Typing
            </span>
            <button
              type="button"
              onClick={toggleTamilTyping}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                typingMode === "tanglish" ? "bg-orange-600" : "bg-slate-300"
              }`}
              aria-label="Toggle Tamil typing"
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform ${
                  typingMode === "tanglish" ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>
          <div className="grid lg:grid-cols-[0.75fr_1.25fr]">
            <aside className="bg-orange-600 p-8 text-white lg:p-10">
              <p className="text-sm font-bold uppercase tracking-[0.25em] text-orange-200">
                Enterprise onboarding
              </p>
              <h1 className="mt-4 text-3xl font-black leading-tight tracking-tight">
                Create your organisation workspace
              </h1>
              <p className="mt-4 text-sm leading-7 text-orange-50/80">
                Register your business profile, contact details, and proof
                documents to start managing moi events.
              </p>

              <div className="mt-8 grid gap-3">
                {[
                  { icon: FaUserTie, text: "Account owner details" },
                  { icon: FaBuilding, text: "Organisation verification" },
                  { icon: FaIdCard, text: "Aadhaar, PAN, and proof upload" },
                ].map(({ icon: Icon, text }) => (
                  <div
                    key={text}
                    className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.08] px-4 py-3"
                  >
                    <Icon className="text-orange-200" />
                    <span className="text-sm font-semibold text-slate-50">
                      {text}
                    </span>
                  </div>
                ))}
              </div>
            </aside>

            <div className="p-5 sm:p-8 lg:p-10">
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
              >
                {(formik) => (
                  <Form className="space-y-8">
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 sm:p-5">
                      <div className="mb-4 flex items-center justify-between gap-3">
                        <div>
                          <p className="text-xs font-bold uppercase tracking-[0.22em] text-orange-600">
                            Step {activeStep + 1} of {STEP_CONFIG.length}
                          </p>
                          <h2 className="mt-1 text-xl font-black tracking-tight text-slate-950">
                            {currentStepConfig.title}
                          </h2>
                          <p className="mt-1 text-sm text-slate-500">
                            {currentStepConfig.description}
                          </p>
                        </div>
                        <div className="hidden rounded-full bg-white px-3 py-2 text-sm font-bold text-slate-600 shadow-sm sm:block">
                          {currentStepConfig.number}
                        </div>
                      </div>

                      <div className="grid gap-3 md:grid-cols-3">
                        {STEP_CONFIG.map((step, index) => {
                          const Icon = step.icon;
                          const isActive = index === activeStep;
                          const isComplete = index < activeStep;

                          return (
                            <button
                              key={step.id}
                              type="button"
                              onClick={() => {
                                if (index <= activeStep) {
                                  setActiveStep(index);
                                }
                              }}
                              className={`rounded-xl border px-4 py-3 text-left transition ${
                                isActive
                                  ? "border-sky-500 bg-white shadow-sm"
                                  : isComplete
                                    ? "border-emerald-200 bg-emerald-50"
                                    : "cursor-default border-slate-200 bg-white/70"
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <span
                                  className={`flex h-9 w-9 items-center justify-center rounded-lg ${
                                    isActive
                                      ? "bg-sky-50 text-sky-700"
                                      : isComplete
                                        ? "bg-emerald-100 text-emerald-700"
                                        : "bg-slate-100 text-slate-500"
                                  }`}
                                >
                                  {isComplete ? <FaCheck className="text-sm" /> : <Icon className="text-sm" />}
                                </span>
                                <div className="min-w-0">
                                  <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
                                    {step.number}
                                  </p>
                                  <p className="truncate text-sm font-bold text-slate-900">
                                    {step.title}
                                  </p>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {activeStep === 0 && (
                      <section>
                        <SectionHeader
                          icon={FaUserTie}
                          title="Owner Details"
                          description="Basic information for the primary enterprise account."
                        />
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                          <FormField label="First Name" name="first_name" forceTamil />
                          <FormField label="Last Name" name="last_name" forceTamil />
                          <FormField label="Mobile Number" name="mobile">
                            <Field name="mobile">
                              {({ field }) => (
                                <input
                                  {...field}
                                  id="mobile"
                                  type="text"
                                  className={`no-translate ${inputClass}`}
                                  onChange={(event) => {
                                    field.onChange(event);
                                    setContactAvailability(null);
                                  }}
                                />
                              )}
                            </Field>
                          </FormField>
                          <FormField label="Email" name="email">
                            <Field name="email">
                              {({ field }) => (
                                <input
                                  {...field}
                                  id="email"
                                  type="email"
                                  className={`no-translate ${inputClass}`}
                                  onChange={(event) => {
                                    field.onChange(event);
                                    setContactAvailability(null);
                                  }}
                                />
                              )}
                            </Field>
                          </FormField>
                          {contactAvailability && (
                            <div
                              className={`md:col-span-2 rounded-xl border px-4 py-3 text-sm font-bold ${
                                contactAvailability.type === "success"
                                  ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                  : "border-red-200 bg-red-50 text-red-700"
                              }`}
                            >
                              {contactAvailability.type === "success" && (
                                <FaCheckCircle className="mr-2 inline-block" />
                              )}
                              {contactAvailability.message}
                            </div>
                          )}
                          <FormField label="Password" name="password">
                            <div className="relative">
                              <Field
                                id="password"
                                name="password"
                                type={showPassword ? "text" : "password"}
                                className={`no-translate pr-11 ${inputClass}`}
                              />
                              <button
                                type="button"
                                className="absolute inset-y-0 right-3 flex items-center text-slate-500 transition hover:text-slate-900"
                                onClick={() => setShowPassword(!showPassword)}
                                aria-label={
                                  showPassword ? "Hide password" : "Show password"
                                }
                              >
                                {showPassword ? (
                                  <FaEyeSlash size={18} />
                                ) : (
                                  <FaEye size={18} />
                                )}
                              </button>
                            </div>
                          </FormField>
                          <FormField label="Plot No." name="plot_no" forceTamil />
                        </div>
                      </section>
                    )}

                    {activeStep === 1 && (
                      <section>
                        <SectionHeader
                          icon={FaMapMarkerAlt}
                          title="Owner Address"
                          description="Address information used for account records."
                        />
                        <div className="grid grid-cols-1 gap-4">
                          <FormField label="Address" name="address" forceTamil />
                          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                            <FormField label="City" name="city">
                              <CitySearchSelect
                                value={formik.values.city}
                                onChange={(city) => formik.setFieldValue("city", city)}
                                onBlur={() => formik.setFieldTouched("city", true)}
                                className={`no-translate ${inputClass}`}
                                placeholder="Search city…"
                              />
                            </FormField>
                            <FormField label="State" name="state" forceTamil />
                            <FormField label="Pincode" name="pincode" noTranslate />
                          </div>
                        </div>
                      </section>
                    )}

                    {activeStep === 2 && (
                      <section className="rounded-2xl border border-slate-200 bg-slate-50 p-5 sm:p-6">
                        <SectionHeader
                          icon={FaBuilding}
                          title="Organisation Details"
                          description="Business information and verification documents."
                        />
                        <div className="grid grid-cols-1 gap-4">
                          <FormField
                            label="Organisation Name"
                            name="organisation_name"
                            forceTamil
                          />
                          <FormField
                            label="Organisation Address"
                            name="organisation_address"
                            forceTamil
                          />
                          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                            <FormField
                              label="Organisation City"
                              name="organisation_city"
                            >
                              <CitySearchSelect
                                value={formik.values.organisation_city}
                                onChange={(city) => formik.setFieldValue("organisation_city", city)}
                                onBlur={() => formik.setFieldTouched("organisation_city", true)}
                                className={`no-translate ${inputClass}`}
                                placeholder="Search city…"
                              />
                            </FormField>
                            <FormField
                              label="Organisation State"
                              name="organisation_state"
                              forceTamil
                            />
                            <FormField
                              label="Organisation Pincode"
                              name="organisation_pincode"
                              noTranslate
                            />
                          </div>
                          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <FormField
                              label="Aadhaar Number"
                              name="organisation_aadhaar_number"
                              noTranslate
                            />
                            <FormField
                              label="PAN Number"
                              name="organisation_pan_number"
                              noTranslate
                            />
                          </div>
                          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div>
                              <label className="mb-1.5 block text-sm font-bold text-slate-700">
                                MSME or other proof document
                              </label>
                              <div className="rounded-xl border border-dashed border-slate-300 bg-white p-4">
                                <DesktopFileUpload
                                  innerText={"Upload File"}
                                  accept={"image/*,application/pdf"}
                                  selectedFileURL={(file) =>
                                    formik.setFieldValue("proof", file)
                                  }
                                />
                                {formik.values.proof && (
                                  <p className="mt-3 flex items-center gap-2 text-sm font-bold text-emerald-600">
                                    <FaCheckCircle />
                                    File uploaded successfully
                                  </p>
                                )}
                              </div>
                              <ErrorMessage
                                name="proof"
                                component="div"
                                className={errorClass}
                              />
                            </div>

                            <div>
                              <label
                                htmlFor="refferral_code"
                                className="mb-1.5 block text-sm font-bold text-slate-700"
                              >
                                Referred By (Optional)
                              </label>
                              <Field
                                id="refferral_code"
                                name="refferral_code"
                                innerRef={refferral_ref}
                                onInput={(event) =>
                                  getRefferralInfo(
                                    event,
                                    formik.setFieldValue,
                                    formik.values,
                                  )
                                }
                                className={`no-translate ${inputClass}`}
                              />
                              {formik.values.refferral_org_name?.length > 0 && (
                                <p className="mt-2 flex items-center gap-2 text-sm font-bold uppercase text-emerald-600">
                                  <FaCheckCircle />
                                  {formik.values.refferral_org_name}
                                </p>
                              )}
                              {refOrgInfo.name?.length > 0 && (
                                <p className="mt-2 flex items-center gap-2 text-sm font-bold uppercase text-emerald-600">
                                  <FaCheckCircle />
                                  {refOrgInfo.name}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </section>
                    )}

                    <div className="flex flex-col gap-3 border-t border-slate-200 pt-6 sm:flex-row sm:items-center sm:justify-between">
                      <button
                        type="button"
                        onClick={() =>
                          setActiveStep((current) => Math.max(current - 1, 0))
                        }
                        disabled={activeStep === 0}
                        className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:border-slate-300 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <FaArrowLeft className="text-xs" />
                        Previous
                      </button>

                      <div className="text-sm text-slate-500">
                        {isLastStep
                          ? "Final step: verify organisation details and submit."
                          : "Complete this section and continue to the next step."}
                      </div>

                      {isLastStep ? (
                        <button
                          type="submit"
                          disabled={formik.isSubmitting}
                          className="flex items-center justify-center gap-2 rounded-lg bg-orange-600 px-4 py-3 text-sm font-bold text-white shadow-lg shadow-orange-100 transition hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          Register Organisation
                          <FaArrowRight className="text-xs" />
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => goToNextStep(formik)}
                          disabled={checkingContactAvailability}
                          className="flex items-center justify-center gap-2 rounded-lg bg-orange-600 px-4 py-3 text-sm font-bold text-white shadow-lg shadow-orange-100 transition hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {checkingContactAvailability ? "Checking..." : "Continue"}
                          <FaArrowRight className="text-xs" />
                        </button>
                      )}
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OperatorRegister;
